import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DSAlert } from '@demandstar/components/alert';
import { DSButton } from '@demandstar/components/button';
import { DSPhoneField } from '@demandstar/components/fields';
import { FlexContainer } from '@demandstar/components/styles';
import { Status } from '@demandstar/components/constants';

import { AddressInfo, AddressLookup } from 'src/components/common/formcomponents/AddressLookup';
import { CheckoutErrorType, useCheckout } from './useCheckout';
import { PaymentInstance, PaymentMethod } from './PaymentMethod';
import { ProductsCopy, ProductsPaths } from 'src/utils/texts/supplier/subscription';
import { appKeys } from 'src/utils/constants';
import { Cart } from '../cart';
import { CompleteOrderModal } from '../ops-actions';
import LogoDSLoader from 'src/assets/images/loader';
import { prorationMismatchState } from 'src/store/recoil/subscriptionState';
import { recoilRegistrationDataState } from 'src/store/recoil/registrationState';
import { scrollToTop } from 'src/utils/helpers';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import { useAuthContext } from 'src/auth/AuthProvider';

interface BillingInfoFormInputs {
  addressInfo: AddressInfo;
}

interface ReviewOrderFormProps {
  /** the default billing address to use */
  defaultAddress?: AddressInfo;
  /** function to call after checkout error */
  handleError?: (type: CheckoutErrorType) => void;
  /** toggles a payment error alert at the top of the page */
  paymentError?: boolean;
  /** to show the form fields validation error */
  invalidFormError?: boolean;
  /** function to call when `Go Back` is clicked */
  previous?: () => void;
  /** function to call after successful payment, used to set Redux navigation state in registration */
  next?: () => void;
}

/**
 * @description shared form for checkout pages
 * @returns JSX.Element
 * @default { paymentError = false }
 *
 * @example <ReviewOrderForm
 * paymentError={false}
 * />
 */
export const ReviewOrderForm = ({
  handleError,
  defaultAddress,
  paymentError = false,
  invalidFormError = false,
  previous,
  next,
}: ReviewOrderFormProps) => {
  const header = `${appKeys.headers.paymentMethod} - Subscription - ${appKeys.name}`;
  const [paymentInstance, setPaymentInstance] = useState<PaymentInstance>();
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const prorationMismatch = useRecoilValue(prorationMismatchState);
  const recoilRegistrationData = useRecoilValue(recoilRegistrationDataState);

  const { checkout } = useCheckout();
  const { auth, refreshAuth } = useAuthContext();
  const history = useHistory();
  const { memberId: registrationMemberId } = recoilRegistrationData;

  const methods = useForm<BillingInfoFormInputs>({
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { getValues, setValue, trigger, formState } = methods;

  const goBack = useMemo(() => {
    return previous
      ? previous
      : () => {
          history.goBack();
        };
  }, [history, previous]);

  const proceed = useMemo(() => {
    return next
      ? next
      : () => {
          history.push(ProductsPaths.CurrentSubscription);
          refreshAuth();
        };
  }, [history, next, refreshAuth]);

  const onError = useCallback(
    (errorType: CheckoutErrorType) => {
      setPaymentProcessing(false);
      if (handleError) {
        handleError(errorType);
      }
    },
    [handleError],
  );

  const handleSubmit = useCallback(
    async (opsConfirmed = false) => {
      setPaymentProcessing(true);

      await trigger();

      try {
        const token: { nonce: string } | undefined = paymentInstance
          ? await paymentInstance.requestPaymentMethod()
          : undefined;

        const billingAddressData: AddressInfo = {
          ...defaultAddress,
          address1: String(getValues('addressInfo.address1')).trim(),
          address2: String(getValues('addressInfo.address2')).trim(),
          addressType: 'BA', // TODO: This should be an enum.
          city: String(getValues('addressInfo.city')).trim(),
          county: getValues('addressInfo.county'),
          country: getValues('addressInfo.country'),
          countryId: getValues('addressInfo.countryId'),
          countyId: getValues('addressInfo.countyId'),
          countyName: getValues('addressInfo.countyName'),
          postalCode: getValues('addressInfo.postalCode'),
          stateId: getValues('addressInfo.stateId'),
          stateName: getValues('addressInfo.stateName'),
          state: getValues('addressInfo.state'),
        };
        billingAddressData.phoneNumber = getValues('phoneNumber');
        if (Object.keys(formState.errors).length === 0) {
          const result = await checkout({
            billingAddress: billingAddressData,
            token,
            memberId: auth?.memberId,
            opsConfirmed,
            registrationMemberId,
          });

          if (result?.status) {
            proceed();
          } else {
            onError(result?.errorType);
          }
        } else {
          onError(CheckoutErrorType.InvalidForm);
        }
      } catch (e) {
        scrollToTop();
        onError(CheckoutErrorType.Other);
      }
    },
    [
      auth,
      checkout,
      defaultAddress,
      formState.errors,
      getValues,
      onError,
      paymentInstance,
      proceed,
      trigger,
      registrationMemberId,
    ],
  );

  // This sets the initial formState equal to the provided address prop
  useEffect(() => {
    if (defaultAddress) {
      Object.keys(defaultAddress).forEach(key => {
        const typedKey = key as keyof typeof defaultAddress;

        if (defaultAddress[typedKey] !== undefined) {
          let fieldValue = defaultAddress[typedKey];

          if (typeof fieldValue === 'string') {
            fieldValue = fieldValue.trim();
          }

          setValue(`addressInfo.${typedKey}`, fieldValue);
        }
      });
    }
  }, [defaultAddress, setValue]);

  return (
    <>
      {paymentError && !prorationMismatch ? (
        <DSAlert header={ProductsCopy.CardErrorHeader} type={Status.Error}>
          {ProductsCopy.CardErrorMessage}
        </DSAlert>
      ) : (
        <p>{ProductsCopy.Checkout}</p>
      )}

      <FormProvider {...methods}>
        <div className='row mb-5'>
          <div className='col-md-12 col-lg-12'>
            <h4 className='no-bottom-margin'>Payment</h4>
            <div className='w-100'>
              <PaymentMethod setPaymentInstance={setPaymentInstance} title={header} />
            </div>
          </div>
          <div className='col-md-12 col-lg-12 pt-4'>
            <h4 className='no-bottom-margin'>Billing Address:</h4>
            {invalidFormError && (
              <DSAlert header={ProductsCopy.InvalidFormErrorHeader} type={Status.Error}>
                {ProductsCopy.InvalidFormErrorMessage}
              </DSAlert>
            )}
            <AddressLookup />
            <DSPhoneField label='Phone Number' name='phoneNumber' />
          </div>
        </div>
      </FormProvider>

      <div className='row'>
        <div className='col-12'>
          <div className='px-6'>
            <Cart isReview={true} />
          </div>
        </div>
      </div>
      <CompleteOrderModal completeOrder={() => handleSubmit(true)} />
      <FlexContainer justifyContent='space-between'>
        <DSButton type='secondary' onClick={goBack} inactive={paymentProcessing}>
          Go Back
        </DSButton>
        <DSButton
          type='primary'
          onClick={() => handleSubmit(false)}
          inactive={paymentProcessing || prorationMismatch !== undefined}
        >
          Complete Order
        </DSButton>
      </FlexContainer>

      {paymentProcessing && (
        <div className='overlay'>
          <div className='progressbox'>
            <div>
              <LogoDSLoader alt='Processing Payment' />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
